import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import api from '../utils/api';
import toast from 'react-hot-toast';
import ContactActions from '../components/ContactActions';

export default function PaymentReceived() {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [emailText, setEmailText] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const { data } = await api.get('/bookings');
      setBookings(data);
    } catch (err) {
      toast.error('Failed to load bookings');
    }
  };

  const bookingOptions = bookings.map(b => ({
    value: b.id, label: `${b.customerName} - ${b.trekName} (${b.id})`
  }));

  const handleBookingSelect = (selected) => {
    const booking = bookings.find(b => b.id === selected.value);
    setSelectedBooking(booking);
    setEmailText('');
    setPdfUrl('');
  };

  const generateEmail = async () => {
    if (!selectedBooking) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/documents/payment-email/${selectedBooking.id}`);
      setEmailText(data.text);
      toast.success('Email text generated!');
    } catch (err) {
      toast.error('Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    if (!selectedBooking) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/documents/pdf-receipt/${selectedBooking.id}`);
      setPdfUrl(data.pdfUrl);
      toast.success('PDF receipt generated!');
    } catch (err) {
      toast.error('Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(emailText);
    toast.success('Email text copied!');
  };

  const sendEmail = () => {
    if (!selectedBooking || !emailText) return;
    const to = selectedBooking.customerEmail || '';
    if (!to) {
      toast.error('No customer email found for this booking');
      return;
    }
    const subject = encodeURIComponent(`Payment Received - ${selectedBooking.trekName} | ${selectedBooking.customerName}`);
    const body = encodeURIComponent(emailText);
    // Open Gmail compose with bookings@garhwalhikes.com as sender
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${subject}&body=${body}&from=${encodeURIComponent('bookings@garhwalhikes.com')}`;
    window.open(gmailUrl, '_blank');
    toast.success('Gmail compose opened! Please attach the PDF receipt manually.');
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">💳 Payment Received - Email & Receipt</h1>

      {/* Booking Selection */}
      <section className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Select Booking</h2>
        <Select options={bookingOptions} onChange={handleBookingSelect}
          placeholder="Search by customer name, trek, or booking ID..." className="text-sm" />

        {selectedBooking && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-gray-500">Customer:</span> <strong>{selectedBooking.customerName}</strong></div>
              <div><span className="text-gray-500">Trek:</span> <strong>{selectedBooking.trekName}</strong></div>
              <div><span className="text-gray-500">Group:</span> <strong>{selectedBooking.groupSize} members</strong></div>
              <div><span className="text-gray-500">Advance:</span> <strong>₹{(selectedBooking.pricing.advanceReceived ?? selectedBooking.pricing.totalAdvance)?.toLocaleString('en-IN')}</strong></div>
            </div>
            <div className="mt-3 pt-3 border-t border-blue-200">
              <ContactActions customerName={selectedBooking.customerName} contactNumber={selectedBooking.contactNumber} />
            </div>
          </div>
        )}
      </section>

      {selectedBooking && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Email Section */}
          <section className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">📧 Email Text</h2>
              <div className="space-x-2">
                <button onClick={generateEmail} disabled={loading}
                  className="bg-primary-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50">
                  Generate
                </button>
                {emailText && (
                  <button onClick={copyEmail} className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700">
                    📋 Copy
                  </button>
                )}
              </div>
            </div>
            {emailText ? (
              <textarea value={emailText} onChange={e => setEmailText(e.target.value)}
                className="w-full h-[500px] p-3 border rounded-lg font-mono text-xs bg-gray-50 resize-none" />
            ) : (
              <div className="h-[500px] flex items-center justify-center text-gray-400 border rounded-lg">
                Click "Generate" to create email text
              </div>
            )}
          </section>

          {/* PDF Section */}
          <section className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">📄 PDF Receipt</h2>
              <div className="space-x-2">
                <button onClick={generatePDF} disabled={loading}
                  className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
                  Generate PDF
                </button>
                {pdfUrl && (
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                    className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700 inline-block">
                    ⬇️ Download
                  </a>
                )}
              </div>
            </div>
            {pdfUrl ? (
              <iframe src={pdfUrl} className="w-full h-[500px] border rounded-lg" title="PDF Preview" />
            ) : (
              <div className="h-[500px] flex items-center justify-center text-gray-400 border rounded-lg">
                Click "Generate PDF" to create receipt
              </div>
            )}
          </section>
        </div>
      )}

      {/* Send Email Button */}
      {emailText && pdfUrl && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border p-6 text-center">
          <p className="text-sm text-gray-500 mb-3">Email text and PDF receipt are ready. Send the email with receipt attached.</p>
          <button onClick={sendEmail}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg text-base font-medium hover:bg-blue-700 inline-flex items-center gap-2">
            ✉️ Send Email
          </button>
          <p className="text-xs text-gray-400 mt-2">Opens Gmail compose for bookings@garhwalhikes.com. Please attach the downloaded PDF manually.</p>
        </div>
      )}
    </div>
  );
}
